<template>
  <CoreLineChart v-if="options" :options="options"></CoreLineChart>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import COLORS from '@/util/colors'
export default {
  props: {
    alerts: {
      type: Boolean,
      required: false,
      default: true
    },
    intervals: {
      type: Array,
      required: false,
      default: undefined
    },
    calculated: {
      type: String,
      required: false,
      default: null
    },
    intervalLengthSeconds: {
      type: Number,
      required: false,
      default: 900
    },
    rampUpMinutes: {
      type: Number,
      required: false,
      default: 30
    },
    rampDownMinutes: {
      type: Number,
      required: false,
      default: 15
    }
  },
  data() {
    return {
      options: false,
      chartData: {
        refreshedAt: '',
        xaxis: [],
        vas: [],
        load: [],
        output: [],
        x_plotlines: [],
        y_plotlines: []
      },
      env: process.env.VUE_APP_ENV
    }
  },
  computed: {
    ...mapGetters([
      'getFacilityTimezone',
      'getFacilityName',
      'getFacilityId',
      'getNcpCeiling'
    ])
  },
  created() {
    if (typeof this.intervals != 'undefined') {
      this.parse()
      this.build()
    }
  },
  methods: {
    parse() {
      // x-axis plotlines are index based, not value based.
      // x-axis points are intervals, so need to calculated the
      //  number of intervals a ramp up and down event covers.

      let numRampUpIntervals = parseInt(
        (this.rampUpMinutes * 60) / this.intervalLengthSeconds
      )
      let numRampDownIntervals = parseInt(
        (this.rampDownMinutes * 60) / this.intervalLengthSeconds
      )

      let inRunEvent = false
      for (var i = 0; i < this.intervals.length; i++) {
        let data = this.intervals[i]

        // compile chart data; used as chart series data
        this.chartData.xaxis.push(
          moment(momentTZ(data.ending).tz(this.getFacilityTimezone)).format(
            'MM/DD  HH:mm'
          )
        )
        this.chartData.vas.push(
          parseFloat(parseFloat(data.totalValueAtStake).toFixed(2))
        )
        this.chartData.load.push(parseFloat(data.loadForecastKw))
        this.chartData.output.push(parseFloat(data.targetOutputKw))

        // ** INTERVAL BASED PLOTLINES ** //
        // if guidance = MUST_RUN | DID_RUN
        //     if (NOT tracking run event already)
        //         track run event
        //         create a plot line at (x - # minute intervals for ramp up)
        //     end if
        // else (guidance not MUST_RUN | DID_RUN )
        //     if (tracking run event already)
        //         stop tracking run event
        //         create a plot line at (x+ # minute intervals for ramp down)
        //     end if
        // end if-else

        let ts = momentTZ(moment()).tz(this.getFacilityTimezone)
        let ending = moment(momentTZ(data.ending).tz(this.getFacilityTimezone))
        // console.log('now', ts.format('MM/DD  HH:mm'))

        if (data.guidance === 'MUST_RUN' || data.guidance === 'DID_RUN') {
          if (!inRunEvent) {
            inRunEvent = true
            let idx = i - numRampUpIntervals

            // console.log(
            //   '%s UP  i=%d   idx=%d',
            //   ending.format('MM/DD  HH:mm'),
            //   i,
            //   idx
            // )
            if (idx >= 0) {
              let plot = {
                color: COLORS.hex.rampUp,
                width: 2,
                value: idx,
                dashStyle: 'ShortDot',
                label: { text: 'RAMP UP', textAlign: 'left' }
              }
              this.chartData.x_plotlines.push(plot)
              // console.log('PLT: Ramp up @', this.chartData.xaxis[idx])

              // check timing for ramp up alert
              // if (currentTime == rampUpTime) { alert }
              let diff = moment.duration(ts.diff(ending))
              // console.log('diff hours', diff.asHours())
              // console.log('diff minutes', diff.minutes())
              if (
                diff.asHours() === 0 &&
                diff.asMinutes >= 0 &&
                diff.asMinutes < 5
              ) {
                if (this.alerts) {
                  this.$toast.info("Ramping up in " + diff.asMinutes() + " minutes.", { timeout: 5000 } )
                }
              }
            }
          }
        } else {
          if (inRunEvent) {
            inRunEvent = false
            let idx = i + numRampDownIntervals - 1 // account for post interval (-1)
            // console.log(
            //   '%s UP  i=%d   idx=%d',
            //   ending.format('MM/DD  HH:mm'),
            //   i,
            //   idx
            // )
            if (idx >= 0 && idx < this.intervals.length) {
              let plot = {
                color: COLORS.hex.rampDown,
                width: 2,
                value: idx,
                dashStyle: 'ShortDot',
                label: { text: 'RAMP DOWN', textAlign: 'left' }
              }
              this.chartData.x_plotlines.push(plot)

              // check timing for ramp up alert
              // if (currentTime == rampUpTime) { alert }
              let diff = moment.duration(ts.diff(ending))
              // console.log('diff hours', diff.asHours())
              // console.log('diff minutes', diff.minutes())
              if (
                diff.asHours() === 0 &&
                diff.asMinutes >= 0 &&
                diff.asMinutes < 5
              ) {
                if (this.alerts) {
                  this.$toast.info("Ramping down in " + diff.asMinutes() + " minutes.", { timeout: 5000 } )
                }
              }
            }
          }
        }
      }

      // ** NON-INTERVAL BASED PLOTLINES ** //
      // NCP ceiling plotline
      let ceiling = this.getNcpCeiling
      if (ceiling != null) {
        if (ceiling >= 0) {
          this.chartData.y_plotlines.push({
            color: '#aaaaaa',
            width: 2,
            value: ceiling,
            dashStyle: 'longdashdot',
            label: {
              text: 'NCP Ceiling (' + ceiling + ' kW)',
              align: 'left',
              style: {
                color: '#aaaaaa',
                fontWeight: `300`
              }
            }
          })
        }
      }

      // final plotline is current time
      let now = this.$_datetimeMethod_formatTimeInZone(
            this.currentTime,
            this.getFacilityTimezone,
            'HH:mm')
      
      // handle debugging in dev ONLY, and only if datatime sent over
      if (this.env != 'production') {
        if (this.calculated != null) {
          now = this.$_datetimeMethod_formatTimeHoursMinutes(this.calculated);
        }
      }
      else { // current time, converted
          now = this.$_datetimeMethod_formatTimeInZone(
            this.currentTime,
            this.getFacilityTimezone,
            'HH:mm')
      }
      
      let plotline = {
        color: COLORS.hex.time,
        width: 3,
        value: this.getIntervalIndexForTime(now),
        dashStyle: 'Solid',
        label: { text: now + ' -- NOW', textAlign: 'left' }
      }
      this.chartData.x_plotlines.push(plotline)
    },
    getIntervalIndexForTime(timestamp) {
      let currentHour = parseInt(timestamp.split(':')[0])
      let currentMinutes = parseInt(timestamp.split(':')[1])

      for (var i = 0; i < this.intervals.length; i++) {
        let data = this.intervals[i]
        let iHour = moment(data.ending).hours()
        if (iHour === currentHour) {
          let lower = moment(data.ending).minutes()
          let upper = lower + parseInt(this.intervalLengthSeconds / 60)
          upper = upper <= 60 ? upper : 60
          if (currentMinutes >= lower && currentMinutes <= upper) {
            return i
          }
        }
      }
      return 0
    },
    build() {
      this.options = {
        title: {
          text: 'Load Forecast (kW), Target Output (kW) and Value'
        },
        subtitle: {
          text: 'Calculated: ' + this.calculated
          // + '<br/>' + '<small>(Click and drag across X axis to zoom)</small>'
        },
        exporting: {
          filename:
            this.getFacilityName.replace(new RegExp(' ', 'g'), '_') +
            '__Guidance'
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value}'
          },
          categories: this.chartData.xaxis,
          crosshair: true,
          plotLines: this.chartData.x_plotlines
        },
        yAxis: [
          {
            // Primary yAxis
            labels: {
              format: '${value:,.0f}',
              style: {
                color: COLORS.hex.value
              }
            },
            title: {
              text: 'Value',
              style: {
                color: COLORS.hex.value
              }
            },
            opposite: true
          },
          {
            // Secondary yAxis
            gridLineWidth: 1,
            labels: {
              format: '{value:,.0f} kW',
              style: {
                color: COLORS.hex.power
              }
            },
            title: {
              text: 'Target Output / Load',
              style: {
                color: COLORS.hex.power
              }
            },
            /* horizontal lines to indicate special events on output axis */
            plotLines: this.chartData.y_plotlines
          }
        ],
        series: [
          {
            type: 'spline',
            name: 'Value',
            data: this.chartData.vas,
            color: COLORS.hex.value,
            yAxis: 0
          },
          {
            type: 'spline',
            name: 'Target Output',
            data: this.chartData.output,
            color: COLORS.hex.power,
            yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          },
          {
            type: 'spline',
            name: 'Load Forecast',
            data: this.chartData.load,
            color: COLORS.hex.load,
            yAxis: 1,
            tooltip: {
              valueSuffix: ' kW'
            }
          }
        ],
        tooltip: {
          shared: true,
          crosshairs: [true, true],
          backgroundColor: COLORS.hex.tooltip,
          borderColor: COLORS.hex.black,
          borderRadius: 10,
          borderWidth: 1,
          formatter: function(d) {
            var rV = '<b>Ending: ' + this.x + '</b><br/>'
            if (!d.isHidden) {
              this.points.forEach(function(d) {
                let val = 0
                if (d.series.name === 'Value') {
                  // val = parse(d.y).toFixed(2)
                  val = '$' + d.y.toLocaleString()
                } else {
                  val = parseInt(d.y).toLocaleString()
                  val += ' kW'
                }
                rV +=
                  '<span style="color:' +
                  d.color +
                  '">\u25CF</span> ' +
                  d.series.name +
                  ': <b> ' +
                  val +
                  '</b><br/>'
              })
            }
            return rV
          }
        }
      }
    }
  }
}
</script>
